/**
 * Local Emotion Classifier - Platform-agnostic FACS Rule Engine
 *
 * Classifies 7 Ekman emotions from MediaPipe blendshapes using:
 * - FACS Action Unit combinations (Ekman & Friesen)
 * - Percentile-based personal calibration (from AlphaBuzz_2)
 * - Dynamic valence/arousal from Russell's circumplex model
 * - EMA temporal smoothing to prevent flickering
 * - Engagement gating (emotions only when user is looking at screen)
 *
 * Zero external dependencies. Works on web and mobile.
 */

import type { EmotionalState, LocalClassifierConfig, CognitiveState, EmotionDetectorConfig } from './types';
import { analyzeCognitiveState, type ScreenAnchor } from './CognitiveStateAnalyzer';
import { LocalEmotionClassifierService } from './LocalEmotionClassifierService';
import { YawnDetectorState } from './classifiers/utils/YawnDetector';
import { computeFaceAndHandMetrics } from './LocalEmotionUtils';
import { EmotionFrameProcessor } from './classifiers/service/EmotionFrameProcessor';
import type { ProcessFrameInput } from './classifiers/service/EmotionFrameProcessor';
import type { CalibrationBaseline } from './classifiers/service/CalibrationMath';
import { applyBaselineDrift } from './classifiers/service/CalibrationMath';
import type { EmotionScore } from './classifiers/service/Scorer';
import { EyeContextAnalyzer, type EyeReliabilityContext } from './classifiers/service/EyeContextAnalyzer';
import { buildEmotionThresholdMaps, type EmotionThresholdMaps } from './classifiers/service/EmotionDetectorRegistry';

// ============================================================================
// FACS RULES — Action Unit combinations per emotion
// Moved to LocalEmotionClassifierService for clean separation
// ============================================================================

// ============================================================================
// CLASSIFIER
// ============================================================================

export class LocalEmotionClassifier {
  private config: Required<LocalClassifierConfig>;
  private calibrationSamples: Record<string, number>[] = [];
  private calibrationStartTime = 0;
  private baseline: CalibrationBaseline | null = null;
  private originalAuMedians: Record<string, number> | null = null;
  private previousBaseline: CalibrationBaseline | null = null;
  /** Stored gaze calibration offset (h/v) - optional */
  private gazeCalibrationOffset: { h: number; v: number } | null = null;
  /** Stored head calibration offset (yaw/pitch proxy) - optional */
  private headCalibrationOffset: { yaw: number; pitch: number } | null = null;
  /** Screen-space anchor captured during calibration (where content lives on screen). */
  private screenCalibrationAnchor: ScreenAnchor | null = null;
  private isCalibrating = false;
  private emotionDetectorConfig: EmotionDetectorConfig = {};
  private emotionThresholds: EmotionThresholdMaps = { minScoreByEmotion: {}, strongInstantScoreByEmotion: {} };
  // Yawn detection — delegated to stateful detector
  private yawnDetector!: YawnDetectorState;
  private YAWN_HOLD_FRAMES = 36;
  private readonly POST_YAWN_COOLDOWN_FRAMES = 20;
  // Frame processor — owns persistence state
  private frameProcessor = new EmotionFrameProcessor();
  /** Ignore low-information frames during calibration (e.g. keys: 0). */
  private readonly CALIBRATION_MIN_KEYS = 12;
  /** Require enough valid frames before finalizing baseline. */
  private readonly CALIBRATION_MIN_VALID_SAMPLES = 40;
  /** Allow extra capture time in low-light/intermittent tracking before giving up. */
  private calibrationWaitLogAt = 0;
  private classifyFrameId = 0;
  /** Post-calibration warm-up: suppress first N frames on mobile to avoid first-frame artifacts */
  private postCalibrationFrameCount = 0;

  private service = new LocalEmotionClassifierService();
  private eyeContextAnalyzer = new EyeContextAnalyzer({
    smoothAlpha: 0.28,
    presenceFallbackMin: 0.35,
  });
  private lastEyeContextLogSignature: string | null = null;
  private readonly EYE_CONTEXT_LOG_EVERY_N_FRAMES = 20;

  constructor(config?: LocalClassifierConfig) {
    this.config = {
      calibrationDuration: config?.calibrationDuration ?? 4000,
      // default to stronger smoothing for stability on website use-case
      smoothingFactor: config?.smoothingFactor ?? 0.15,
      smileHysteresis: config?.smileHysteresis ?? 0.02,
      frownHysteresis: config?.frownHysteresis ?? 0.08,
      // persistence: require N consecutive frames of same winner before committing emotion
      persistenceFrames: config?.persistenceFrames ?? 2,
      persistenceMs: config?.persistenceMs ?? 0,
      yawnThreshold: config?.yawnThreshold ?? 0.45,
      engine: config?.engine ?? 'default',
      suppressWhenUnengaged: config?.suppressWhenUnengaged ?? true,
      stabilityWindow: config?.stabilityWindow ?? 20,
      isMobileRuntime: config?.isMobileRuntime ?? false,
      emotionDetectors: config?.emotionDetectors ?? {},
    };
    this.emotionDetectorConfig = this.config.emotionDetectors;
    this.emotionThresholds = buildEmotionThresholdMaps(this.emotionDetectorConfig.emotions);
    const yawnConfig = this.emotionDetectorConfig.yawn;
    this.YAWN_HOLD_FRAMES = yawnConfig?.holdFrames ?? 36;
    this.yawnDetector = new YawnDetectorState({
      isMobileRuntime: this.config.isMobileRuntime,
      yawnThreshold: yawnConfig?.threshold ?? 0.38,
      yawnHoldFramesLimit: this.YAWN_HOLD_FRAMES,
      yawnMarThreshold: this.config.isMobileRuntime
        ? (yawnConfig?.marThresholdMobile ?? 0.55)
        : (yawnConfig?.marThresholdDesktop ?? 0.66),
      yawnEarThreshold: this.config.isMobileRuntime
        ? (yawnConfig?.earThresholdMobile ?? 0.26)
        : (yawnConfig?.earThresholdDesktop ?? 0.23),
      yawnWindow: yawnConfig?.window ?? 4,
      yawnRequired: yawnConfig?.requiredFrames ?? 2,
    });
  }
  /**
   * Start calibration phase. Call this when session begins.
   */
  startCalibration(): void {
    this.calibrationSamples = [];
    this.calibrationStartTime = Date.now();
    this.previousBaseline = this.baseline;
    this.baseline = null;
    this.isCalibrating = true;
    this.calibrationWaitLogAt = 0;
    this.gazeCalibrationOffset = null;
    this.headCalibrationOffset = null;
    this.screenCalibrationAnchor = null;
    this.postCalibrationFrameCount = 0;
    this.frameProcessor.reset();
    this.eyeContextAnalyzer.reset();
    this.lastEyeContextLogSignature = null;
  }

  /**
   * Main classify method. Feed blendshapes every frame.
   * During calibration, collects samples and returns null.
   * After calibration, returns classified EmotionalState.
   */
  classify(
    blendshapes: Record<string, number>,
    _landmarks?: Array<{ x: number; y: number; z: number; i?: number; visibility?: number }>,
    _headRotation?: { yaw: number; pitch: number; roll: number },
    handNearFace?: boolean,
    handLandmarks?: Array<{ x: number; y: number; z?: number }>,
  ): EmotionalState | null {
    const eyeContext = this.computeEyeReliabilityContext(_landmarks, blendshapes);

    // Calibration phase: collect samples
    if (this.isCalibrating) {
      const elapsed = Date.now() - this.calibrationStartTime;
      const keyCount = Object.keys(blendshapes).length;
      if (keyCount >= this.CALIBRATION_MIN_KEYS) {
        this.calibrationSamples.push({
          ...blendshapes,
          __eyeReliability: eyeContext.eyeReliability,
          __eyeOccluded: eyeContext.eyeOccluded ? 1 : 0,
        });
      }

      // Finalize as soon as we have enough valid samples (no minimum wait time)
      if (this.calibrationSamples.length < this.CALIBRATION_MIN_VALID_SAMPLES) {
        if (Date.now() - this.calibrationWaitLogAt > 1000) {
          this.calibrationWaitLogAt = Date.now();
          console.info('[EIE][CAL] collecting samples', {
            validSamples: this.calibrationSamples.length,
            minRequired: this.CALIBRATION_MIN_VALID_SAMPLES,
            elapsed,
            keyCount,
          });
        }
        return null;
      }

      this.finalizeCalibration();
    }

    // Mobile warm-up: force neutral for first 3 frames after calibration
    // to avoid first-frame angry/sad artifacts from residual AU noise
    if (this.config.isMobileRuntime && this.postCalibrationFrameCount < 3) {
      this.postCalibrationFrameCount++;
      return {
        emotion: 'neutral',
        valence: 0,
        arousal: 0,
        confidence: 0.5,
        timestamp: Date.now(),
        cognitive: 'engaged' as any,
        cognitiveConfidence: 0.85,
        blendshapes,
      };
    }

    console.warn('[EIE] classify called, keys:', Object.keys(blendshapes).length);

    // On desktop, skip cognitive gating — camera angle varies.
    // Only gate if no face detected (empty blendshapes).
    const hasFace = Object.keys(blendshapes).length > 0;
    if (!hasFace) {
      return {
        emotion: 'neutral',
        valence: 0,
        arousal: 0,
        confidence: 0,
        timestamp: Date.now(),
        cognitive: 'out_of_frame',
        cognitiveConfidence: 1,
        blendshapes,
      };
    }

    // ── Per-user zero-centering ──
    // Subtract each AU's calibrated resting median so that all downstream
    // scoring and boost/guard thresholds operate on the delta-from-neutral,
    // not the absolute blendshape value.  This normalises across diverse
    // face shapes, skin tones, ages, and lighting conditions.
    const rawBlendshapes = blendshapes;
    const frameId = ++this.classifyFrameId;
    const activeMedians = this.getActiveAUMedians(eyeContext);
    if (activeMedians) {
      blendshapes = this.zeroCenterBlendshapes(rawBlendshapes, activeMedians);
    }
    const _log_early = typeof window !== 'undefined' && (window as any)._log ? (window as any)._log : console.log;
    const profileSource = eyeContext.eyeOccluded && this.baseline?.eyeContextProfiles?.eyeOccluded
      ? 'occluded-cal'
      : eyeContext.eyeOccluded ? 'global (no-occluded-cal)' : 'clear-cal';
    const eyeContextLogSignature = `${eyeContext.eyeOccluded ? 'Y' : 'N'}:${profileSource}`;
    const shouldLogEyeContext = this.lastEyeContextLogSignature === null
      || frameId <= 3
      || this.lastEyeContextLogSignature !== eyeContextLogSignature
      || frameId % this.EYE_CONTEXT_LOG_EVERY_N_FRAMES === 0;
    if (shouldLogEyeContext) {
      _log_early(`[EIE][EYE-CONTEXT] eyeOcc=${eyeContext.eyeOccluded ? 'Y' : 'N'} eyeRel=${eyeContext.eyeReliability.toFixed(3)} leftVis=${eyeContext.leftVisibility.toFixed(3)} rightVis=${eyeContext.rightVisibility.toFixed(3)} light=${eyeContext.ambientLightNorm.toFixed(3)} lowL=${eyeContext.lowLightSeverity.toFixed(3)} visSrc=${eyeContext.visibilitySource} profileSource=${profileSource}`);
      this.lastEyeContextLogSignature = eyeContextLogSignature;
    }

    const { lipCurveSad, mar, ear, handNearMouth } = computeFaceAndHandMetrics(_landmarks, handLandmarks, rawBlendshapes, _headRotation);

    const mouthOpen = this.service.isMouthOpen(blendshapes);
    // For focus suppression, use a stricter mouth-open gate: slight lip parting should not block focused.
    const mouthOpenForFocus = mouthOpen && mar > 0.42;

    // --- LOGIC & DEBUG LOGS ENHANCED ---
    const _log = typeof window !== 'undefined' && (window as any)._log ? (window as any)._log : console.log;
    const verboseLogs = typeof window !== 'undefined' && (window as any).__EIE_VERBOSE_LOGS === true;
    const _vlog = (...args: any[]) => { if (verboseLogs) (_log as any)(...args); };
    _vlog(`[EIE][FRAME] id=${frameId} bsKeys=${Object.keys(blendshapes).length} rawKeys=${Object.keys(rawBlendshapes).length} headPitch=${((_headRotation?.pitch ?? 0)).toFixed(4)} headYaw=${((_headRotation?.yaw ?? 0)).toFixed(4)} eyeRel=${eyeContext.eyeReliability.toFixed(3)} eyeOcc=${eyeContext.eyeOccluded ? 'Y' : 'N'} light=${eyeContext.ambientLightNorm.toFixed(3)} lowL=${eyeContext.lowLightSeverity.toFixed(3)} handFace=${handNearFace ? 'Y' : 'N'} handMouth=${handNearMouth ? 'Y' : 'N'}`);

    // On mobile (IMAGE mode), each frame is independent — use raw blendshapes for yawn
    // so the large absolute jaw-open signal is not lost to zeroCentering/noise-floor.
    const yawnBlendshapes = this.config.isMobileRuntime ? rawBlendshapes : blendshapes;
    const yawnResult = this.yawnDetector.evaluate({
      blendshapes: yawnBlendshapes,
      mar,
      ear,
      handNearFace: !!handNearFace,
      handNearMouth: !!handNearMouth,
      lowLightSeverity: eyeContext.lowLightSeverity,
    });
    let { yawnScore, yawnDetected } = yawnResult;

    if (yawnDetected) {
      (blendshapes as any).__lastYawnDetected = true;
      this.frameProcessor.updatePostYawnCooldown(this.POST_YAWN_COOLDOWN_FRAMES);
    }

    // Score all emotions
    const scores = this.scoreAllEmotions(blendshapes, yawnDetected, yawnScore, eyeContext);

    // Hard guard: confirmed yawn is incompatible with surprise — suppress it fully
    // Hard guard: confirmed yawn suppresses emotions driven by wide-open mouth/stretch.
    // Surprise, happy (mouthStretch → smile scorer) and angry (lip tension) are all
    // false-positive artifacts of jaw opening, not genuine emotional states.
    if (yawnDetected) {
      for (const s of scores) {
        if (s.emotion === 'surprise' || s.emotion === 'happy' || s.emotion === 'angry') {
          s.score = 0; s.confidence = 0;
        }
      }
    }

    // LOG: tutti gli score raw delle emozioni
    _log(`[EIE][SCORES] ${scores.map(s => `${s.emotion}:${s.score.toFixed(3)}`).join(' ')} | angry:${(blendshapes['browDownLeft'] ?? 0).toFixed(2)}/${(blendshapes['browDownRight'] ?? 0).toFixed(2)} sad:${(blendshapes['mouthFrownLeft'] ?? 0).toFixed(2)}/${(blendshapes['mouthFrownRight'] ?? 0).toFixed(2)} happy:${(blendshapes['mouthSmileLeft'] ?? 0).toFixed(2)}/${(blendshapes['mouthSmileRight'] ?? 0).toFixed(2)} MAR:${mar.toFixed(2)} EAR:${ear.toFixed(2)}`);

    const frameInput: Omit<ProcessFrameInput, 'state'> = {
      scores,
      blendshapes,
      rawBlendshapes,
      baseline: this.baseline,
      adaptiveConfig: this.baseline?.adaptiveConfig,
      yawnDetected,
      yawnScore,
      eyeContext,
      handNearFace: !!handNearFace,
      handNearMouth: !!handNearMouth,
      mouthOpen,
      mouthOpenForFocus,
      lipCurveSad,
      mar,
      frameId,
      isMobileRuntime: this.config.isMobileRuntime,
      emotionThresholds: this.emotionThresholds,
      config: { smoothingFactor: this.config.smoothingFactor, persistenceFrames: this.config.persistenceFrames ?? 3 },
      helpers: {
        averagePositiveExcursion: this.averagePositiveExcursion.bind(this),
        isWithinNeutralRange: this.isWithinNeutralRange.bind(this),
        isEmotionWeakRelativeToBaseline: this.isEmotionWeakRelativeToBaseline.bind(this),
        decideFocus: this.service.decideFocus.bind(this.service),
        computeStability: this.computeStability.bind(this),
        duchenneFactor: this.duchenneFactor.bind(this),
      },
    };

    const result = this.frameProcessor.process(frameInput);

    // if (result?.emotion === 'neutral' && this.baseline?.auMedians && this.baseline?.auRanges && this.originalAuMedians) {
    //   const activeMedians = this.getActiveAUMedians(eyeContext);
    //   if (activeMedians) {
    //     applyBaselineDrift(activeMedians, this.baseline.auRanges, this.originalAuMedians, rawBlendshapes);
    //   }
    // }

    return result;
  }

  getIsCalibrating(): boolean { return this.isCalibrating; }

  getCalibrationProgress(): { samples: number; required: number; progress: number } | null {
    if (!this.isCalibrating) return null;
    const samples = this.calibrationSamples.length;
    const required = this.CALIBRATION_MIN_VALID_SAMPLES;
    return { samples, required, progress: Math.min(1, samples / required) };
  }

  /** Get computed baseline (null if not yet calibrated) */
  getBaseline(): CalibrationBaseline | null { return this.baseline; }

  /** Reset classifier state */
  reset(): void {
    this.calibrationSamples = [];
    this.baseline = null;
    this.isCalibrating = false;
    this.headCalibrationOffset = null;
    this.yawnDetector.reset();
    this.frameProcessor.reset();
    this.eyeContextAnalyzer.reset();
  }

  /** Get cognitive state separately (useful for UI) */
  getCognitiveState(
    landmarks?: Array<{ x: number; y: number; z: number; i?: number; visibility?: number }>,
    blendshapes?: Record<string, number>,
    headRotation?: { yaw: number; pitch: number; roll: number },
  ): CognitiveState {
    // Ensure landmarks are in indexed order expected by analyzer (lm[33], lm[263], etc.).
    // Input may be an array of objects with `.i` index or already an indexed array.
    if (!landmarks || landmarks.length === 0) {
      return analyzeCognitiveState(
        [],
        blendshapes,
        headRotation,
        this.gazeCalibrationOffset ?? undefined,
        this.headCalibrationOffset ?? undefined,
        this.screenCalibrationAnchor ?? undefined,
        { isMobileRuntime: this.config.isMobileRuntime },
      );
    }

    let ordered: { x: number; y: number }[] = [];
    try {
      // If landmarks include `i` indices, place them at their slot
      const hasIndex = landmarks.some((l: any) => typeof (l as any).i === 'number');
      if (hasIndex) {
        const maxIdx = Math.max(...(landmarks as any[]).map((l: any) => l.i));
        ordered = new Array(Math.max(469, maxIdx + 1)).fill(null).map(() => ({ x: 0, y: 0 }));
        for (const lm of landmarks as any[]) {
          if (typeof lm.i === 'number') ordered[lm.i] = { x: lm.x, y: lm.y };
        }
      } else {
        // assume already ordered by index
        ordered = (landmarks as any[]).map(l => ({ x: l.x, y: l.y }));
      }
    } catch (e) {
      ordered = (landmarks as any[]).map(l => ({ x: l.x, y: l.y }));
    }

    return analyzeCognitiveState(
      ordered ?? [],
      blendshapes,
      headRotation,
      this.gazeCalibrationOffset ?? undefined,
      this.headCalibrationOffset ?? undefined,
      this.screenCalibrationAnchor ?? undefined,
      { isMobileRuntime: this.config.isMobileRuntime },
    );
  }

  /** Set a gaze calibration offset (h/v) so cognitive analysis treats that vector as 'screen center' */
  setGazeCalibrationOffset(offset: { h: number; v: number } | null): void {
    this.gazeCalibrationOffset = offset;
    if (this.baseline) this.baseline.gazeOffset = offset ?? undefined;
  }

  getGazeCalibrationOffset(): { h: number; v: number } | null {
    return this.gazeCalibrationOffset;
  }
 

  setHeadCalibrationOffset(offset: { yaw: number; pitch: number } | null): void {
    this.headCalibrationOffset = offset;
  }

  getHeadCalibrationOffset(): { yaw: number; pitch: number } | null {
    return this.headCalibrationOffset;
  }

  setScreenCalibrationAnchor(anchor: ScreenAnchor | null): void {
    this.screenCalibrationAnchor = anchor;
  }

  getScreenCalibrationAnchor(): ScreenAnchor | null {
    return this.screenCalibrationAnchor;
  }

  // ============================================================================
  // PRIVATE
  // ============================================================================

  /** Subtract per-AU calibrated median from live blendshapes so that all
   *  scoring operates on _delta-from-neutral_ rather than absolute values.
   *  This makes thresholds universally accurate across face shapes. */
  private zeroCenterBlendshapes(bs: Record<string, number>, auMedians: Record<string, number>): Record<string, number> {
    const centred: Record<string, number> = { ...bs };
    const auRanges = this.config.isMobileRuntime ? this.baseline?.auRanges : null;
    for (const key of Object.keys(auMedians)) {
      if (centred[key] !== undefined) {
        centred[key] = centred[key] - auMedians[key];
        // Mobile soft thresholding: zero-out signals within the calibration noise floor
        if (auRanges) {
          const range = auRanges[key];
          if (range) {
            const noiseFloor = (range.p90 - range.p10) * 0.5;
            if (Math.abs(centred[key]) < noiseFloor) {
              centred[key] = 0;
            }
          }
        }
      }
    }
    return centred;
  }

  private finalizeCalibration(): void {
    const result = this.service.finalizeCalibration(this.calibrationSamples);
    if (!result) {
      this.baseline = this.previousBaseline;
      this.isCalibrating = false;
      this.previousBaseline = null;
      this.calibrationWaitLogAt = 0;
      return;
    }

    this.baseline = result.baseline;
    this.originalAuMedians = { ...result.baseline.auMedians };
    this.frameProcessor.updateBaselineStability(result.baselineStability);
    console.log(`[EIE] baseline stability: ${result.baselineStability.toFixed(6)}`);
    try {
      const excursions = this.getBaselineExcursions();
      const topExcursions = Object.entries(excursions)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 12)
        .map(([k, v]) => `${k}:${v.toFixed(4)}`)
        .join(' ');
      console.info('[EIE][CAL][PERSONALIZATION] top positive excursions:', topExcursions);
      console.info('[EIE][CAL][PERSONALIZATION] key thresholds:', {
        sadFrownStrong: this.personalizedAvgThreshold(['mouthFrownLeft', 'mouthFrownRight'], 0.14, 0.10),
        sadFrownMedium: this.personalizedAvgThreshold(['mouthFrownLeft', 'mouthFrownRight'], 0.12, 0.08),
        sadFrownSubtle: this.personalizedAvgThreshold(['mouthFrownLeft', 'mouthFrownRight'], 0.055, 0.04),
        sadShrugStrong: this.personalizedThreshold('mouthShrugLower', 0.42, 0.34),
        sadShrugProfile: this.personalizedThreshold('mouthShrugLower', 0.35, 0.28),
        sadLookDownSupport: this.personalizedAvgThreshold(['eyeLookDownLeft', 'eyeLookDownRight'], 0.08, 0.05),
        sadBrowInnerSupport: this.personalizedThreshold('browInnerUp', 0.08, 0.05),
      });
    } catch (err) {
      console.warn('[EIE][CAL][PERSONALIZATION] diagnostics failed', err);
    }

    this.calibrationSamples = [];
    this.isCalibrating = false;
    this.previousBaseline = null;
    this.calibrationWaitLogAt = 0;
  }

  private isWithinNeutralRange(bs: Record<string, number>): boolean {
    return this.service.isWithinNeutralRange(this.baseline, bs, this.config);
  }

  private isEmotionWeakRelativeToBaseline(bs: Record<string, number>, emotion: string): boolean {
    return this.service.isEmotionWeakRelativeToBaseline(this.baseline, bs, emotion);
  }

  private positiveExcursionFromBaseline(auKey: string): number {
    const range = this.baseline?.auRanges?.[auKey];
    if (!range) return 0;
    const median = this.baseline?.auMedians?.[auKey] ?? ((range.p10 + range.p90) / 2);
    return Math.max(0, range.p90 - median);
  }

  private averagePositiveExcursion(auKeys: string[]): number {
    if (auKeys.length === 0) return 0;
    const total = auKeys.reduce((sum, key) => sum + this.positiveExcursionFromBaseline(key), 0);
    return total / auKeys.length;
  }

  private getBaselineExcursions(): Record<string, number> {
    const keys = Object.keys(this.baseline?.auRanges ?? {});
    const out: Record<string, number> = {};
    for (const key of keys) {
      out[key] = this.positiveExcursionFromBaseline(key);
    }
    return out;
  }

  private personalizedThreshold(auKey: string, fallback: number, minValue = 0): number {
    const excursion = this.positiveExcursionFromBaseline(auKey);
    return Math.max(minValue, fallback + excursion * 0.55);
  }

  private personalizedAvgThreshold(auKeys: string[], fallback: number, minValue = 0): number {
    if (auKeys.length === 0) return fallback;
    const avg = auKeys.reduce((sum, key) => sum + this.personalizedThreshold(key, fallback, minValue), 0) / auKeys.length;
    return Math.max(minValue, avg);
  }

  private scoreAllEmotions(
    bs: Record<string, number>,
    yawnDetected?: boolean,
    yawnScore?: number,
    eyeContext?: EyeReliabilityContext,
  ): EmotionScore[] {
    return this.service.scoreAllEmotions(
      this.baseline,
      bs,
      yawnDetected,
      yawnScore,
      eyeContext ? {
        eyeReliability: eyeContext.eyeReliability,
        eyeOccluded: eyeContext.eyeOccluded,
        hasOccludedCalibration: !!this.baseline?.eyeContextProfiles?.eyeOccluded,
      } : undefined,
      this.emotionDetectorConfig,
    );
  }

  /** Compute average variance of key blendshapes across recent frames */
  private computeStability(): number {
    return this.service.computeStability(this.frameProcessor.getRecentFrames());
  }

  /** Duchenne smile factor: genuine smile involves cheek raise + eye squint */
  private duchenneFactor(bs: Record<string, number>): number {
    return this.service.duchenneFactor(bs);
  }

  private getActiveAUMedians(eyeContext: EyeReliabilityContext): Record<string, number> | null {
    if (!this.baseline?.auMedians) return null;
    if (eyeContext.eyeOccluded) {
      return this.baseline.eyeContextProfiles?.eyeOccluded?.auMedians ?? this.baseline.auMedians;
    }
    return this.baseline.eyeContextProfiles?.eyeClear?.auMedians ?? this.baseline.auMedians;
  }

  private computeEyeReliabilityContext(
    landmarks?: Array<{ x: number; y: number; z: number; i?: number; visibility?: number }>,
    blendshapes?: Record<string, number>,
  ): EyeReliabilityContext {
    return this.eyeContextAnalyzer.analyze(
      landmarks,
      blendshapes,
    );
  }

}
